import React from 'react';
import { Star, ExternalLink, User } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { contactInfo } from '../data/mock';

const Reviews = () => {
  const { t } = useLanguage();

  // Mock reviews - ces données seraient normalement extraites de Google My Business API
  const reviews = [
    {
      id: 1,
      author: "Sophie M.",
      rating: 5,
      date: "Il y a 2 semaines",
      text: "Excellent service! Les scooters électriques sont en parfait état et très faciles à utiliser. Le personnel est très accueillant et professionnel. Je recommande vivement!",
      avatar: null
    },
    {
      id: 2,
      author: "Marco R.",
      rating: 5,
      date: "Il y a 1 mois",
      text: "Super expérience avec CleanRide! Le buggy était incroyable pour explorer Bora Bora. Pas de caution demandée, juste la carte d'identité. Service rapide et efficace.",
      avatar: null
    },
    {
      id: 3,
      author: "Emma L.",
      rating: 5,
      date: "Il y a 3 semaines",
      text: "Location parfaite! Les véhicules sont bien entretenus, les casques sont fournis et l'assurance est incluse. Prix très corrects. On a adoré notre séjour grâce à CleanRide!",
      avatar: null
    },
    {
      id: 4,
      author: "Thomas B.",
      rating: 5,
      date: "Il y a 1 semaine",
      text: "Très satisfait de la location de la moto électrique. Silencieuse, puissante et écologique. Le contact WhatsApp est très réactif. Parfait pour découvrir l'île!",
      avatar: null
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.reviews.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            {t.reviews.subtitle}
          </p>
          
          {/* Rating Badge */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="flex items-center bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-3 rounded-full shadow-lg">
              <Star className="w-6 h-6 fill-white mr-2" />
              <span className="text-2xl font-bold">5.0</span>
              <span className="text-sm ml-2 opacity-90">/ 5</span>
            </div>
            <span className="text-gray-600 font-medium">Google Reviews</span>
          </div>
        </div>

        {/* Google My Business Card */}
        <div className="max-w-6xl mx-auto mb-12">
          <Card className="overflow-hidden shadow-2xl border-2 border-[#0077FF]">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    CleanRide Rental - Bora Bora
                  </h3>
                  <p className="text-gray-600 mb-4">
                    E-scooter, E-moto & Buggy Rent - Location
                  </p>
                  <div className="flex items-center justify-center md:justify-start gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                    ))}
                    <span className="ml-2 text-gray-700 font-semibold">5.0/5</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Basé sur les avis vérifiés Google
                  </p>
                </div>
                
                <div className="flex flex-col gap-3">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-[#0077FF] to-[#0055CC] hover:from-[#0055CC] hover:to-[#0077FF] text-white font-bold shadow-xl"
                    onClick={() => window.open(contactInfo.googleMapsUrl, '_blank')}
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    {t.reviews.seeMore}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {reviews.map((review) => (
            <Card key={review.id} className="hover:shadow-xl transition-shadow duration-300 border-2 border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-[#0077FF] to-[#0055CC] p-3 rounded-full flex-shrink-0">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-gray-900">{review.author}</h4>
                      <span className="text-xs text-gray-500">{review.date}</span>
                    </div>
                    <div className="flex items-center mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star} 
                          className={`w-4 h-4 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                        />
                      ))}
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {review.text}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="mt-16 flex flex-wrap justify-center gap-12 items-center">
          <div className="text-center">
            <div className="text-4xl font-bold text-[#0077FF] mb-2">500+</div>
            <div className="text-sm text-gray-600 font-medium">Clients satisfaits</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#0077FF] mb-2">5.0★</div>
            <div className="text-sm text-gray-600 font-medium">Note moyenne Google</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#0077FF] mb-2">100%</div>
            <div className="text-sm text-gray-600 font-medium">Véhicules électriques</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#0077FF] mb-2">0€</div>
            <div className="text-sm text-gray-600 font-medium">Caution demandée</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
